import { useMemo } from 'react'
import usePersonalPortalHref from 'hooks/usePersonalPortalHref'
import { useAppSelector } from 'hooks/reduxHooks'
import { getTimeWindowByDate } from 'lib/datetime/dateUtils'
import config from 'constants/config'
import { toOrdinal } from 'lib/maths/mathUtils'
import { getPlural } from 'lib/string/pluralize'

/**
 * Dynamic societe variables used to interpolate societe content text.
 */
export interface SocieteVariables {
  /** Number of bookings required to unlock Société Gold - Default 5 */
  bookingThreshold: number
  /** Ordinal number of bookings required to unlock Société Gold - e.g 'fifth', '11th', '21st' */
  bookingThresholdOrdinal: string
  /** Plural form of "booking" matching `bookingThreshold` — e.g. "booking" for 1, "bookings" otherwise. */
  bookingPluralText: string
  /** The end date of the qualification derived from employee activation date - Default 12 months later */
  qualificationEndDate: string
  /** Absolute URL of the LE consumer-portal Société page. */
  societePageHref: string
}

export function useSocieteVariables(): SocieteVariables {
  const defaultThreshold = config.businessTraveller.defaultSocieteGold.threshold
  const personalPortalHref = usePersonalPortalHref()
  const societeSetting = useAppSelector(state => state.businessTraveller.businessSettings?.businessSettings?.societe?.value)
  const employeeActivatedAt = useAppSelector(state => state.businessTraveller.currentBusinessAccount?.employee?.activatedAt)

  const societeVariables = useMemo(() => {
    const { endDate } = getTimeWindowByDate(employeeActivatedAt ?? new Date())
    const bookingThreshold = societeSetting?.gold.threshold ?? defaultThreshold

    return {
      bookingThreshold,
      bookingThresholdOrdinal: toOrdinal(bookingThreshold),
      bookingPluralText: getPlural('booking', bookingThreshold),
      qualificationEndDate: endDate,
      societePageHref: `${personalPortalHref}/societe`,
    }
  }, [societeSetting, employeeActivatedAt, personalPortalHref, defaultThreshold])

  return societeVariables
}
